// src/components/KenoBall.jsx - Enhanced & Cleaned

import React, { useMemo } from "react";

/* =========================
   SIZE CONFIG (Increased)
========================= */
const BALL_SIZES = {
  lg: 80,   // was 70
  md: 56,   // was 48
  sm: 46,   // was 40
  xs: 40,   // was 36
  xxs: 36,  // was 32
};

/* =========================
   COLOR LOGIC
========================= */
export const getKenoBallColor = (num) => {
  if (!num) return "#6B7280";

  if (num <= 10) return "#EF4444";
  if (num <= 20) return "#F97316";
  if (num <= 30) return "#FBBF24";
  if (num <= 40) return "#10B981";
  if (num <= 50) return "#3B82F6";
  if (num <= 60) return "#8B5CF6";
  if (num <= 70) return "#EC4899";

  return "#14B8A6";
};

/* =========================
   COMPONENT
========================= */
const KenoBall = React.memo(
  ({
    number,
    size = "md",
    isSelected = false,
    isDrawn = false,
    isCurrent = false,
    shouldRotate = false,
    onClick = null,
  }) => {
    const baseColor = getKenoBallColor(number);

    /* =========================
       STATE COLORS
    ========================= */
    const { displayColor, textColor } = useMemo(() => {
      if (isCurrent) return { displayColor: "#DC2626", textColor: "#fff" };
      if (isDrawn) return { displayColor: "#059669", textColor: "#fff" };
      if (isSelected) return { displayColor: "#0c0101", textColor: "#fff" };

      return { displayColor: baseColor, textColor: "#250186" };
    }, [isCurrent, isDrawn, isSelected, baseColor]);

    /* =========================
       SIZE CALCULATION
    ========================= */
    const {
      baseSize,
      outerRing,
      whiteRing,
      innerRing,
      fontSize,
    } = useMemo(() => {
      const base = BALL_SIZES[size] || BALL_SIZES.md;

      return {
        baseSize: base,
        outerRing: Math.max(2, base * 0.12),
        whiteRing: Math.max(1, base * 0.04),
        innerRing: Math.max(1, base * 0.03),
        fontSize: base * 0.34,
      };
    }, [size]);

    const isClickable = onClick && !isCurrent && !isDrawn;

    const handleClick = () => {
      if (isClickable) onClick(number);
    };

    /* =========================
       RENDER
    ========================= */
    return (
      <div
        onClick={handleClick}
        style={{
          position: "relative",
          width: baseSize,
          height: baseSize,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isClickable ? "pointer" : "default",
          flexShrink: 0,
          userSelect: "none",
          animation: shouldRotate ? "ballRotate 0.6s ease-in-out" : "none",
          transition: "transform 0.2s ease",
        }}
        onMouseEnter={(e) => {
          if (isClickable) e.currentTarget.style.transform = "scale(1.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        {/* OUTER COLOR */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            backgroundColor: displayColor,
          }}
        />

        {/* WHITE RING */}
        <div
          style={{
            position: "absolute",
            top: outerRing,
            left: outerRing,
            width: baseSize - 2 * outerRing,
            height: baseSize - 2 * outerRing,
            borderRadius: "50%",
            backgroundColor: "#fff",
            border: "1px solid #e5e7eb",
          }}
        />

        {/* INNER COLOR */}
        <div
          style={{
            position: "absolute",
            top: outerRing + whiteRing,
            left: outerRing + whiteRing,
            width: baseSize - 2 * (outerRing + whiteRing),
            height: baseSize - 2 * (outerRing + whiteRing),
            borderRadius: "50%",
            backgroundColor: displayColor,
          }}
        />

        {/* CENTER */}
        <div
          style={{
            position: "absolute",
            top: outerRing + whiteRing + innerRing,
            left: outerRing + whiteRing + innerRing,
            width:
              baseSize - 2 * (outerRing + whiteRing + innerRing),
            height:
              baseSize - 2 * (outerRing + whiteRing + innerRing),
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              isCurrent || isDrawn || isSelected
                ? `radial-gradient(circle at 35% 35%, ${displayColor}40, #ffffff 80%)`
                : `radial-gradient(circle at 35% 35%, #ffffff, #f3f4f6 80%)`,
          }}
        >
          {/* SHINE */}
          <div
            style={{
              position: "absolute",
              top: "-10%",
              left: "-10%",
              width: "55%",
              height: "28%",
              backgroundColor: "#fff",
              opacity: 0.15,
              filter: "blur(2px)",
              borderRadius: "50%",
              transform: "rotate(-25deg)",
            }}
          />

          {/* NUMBER */}
          <span
            style={{
              fontWeight: 800,
              fontSize: fontSize,
              color: textColor,
              zIndex: 1,
              textShadow:
                isSelected || isDrawn || isCurrent
                  ? "0 1px 2px rgba(0,0,0,0.4)"
                  : "0 1px 2px rgba(255,255,255,0.5)",
            }}
          >
            {number}
          </span>
        </div>

        {/* ANIMATION */}
        <style>
          {`
            @keyframes ballRotate {
              0% { transform: rotate(0deg) scale(1); }
              25% { transform: rotate(90deg) scale(1.06); }
              50% { transform: rotate(180deg) scale(1.1); }
              75% { transform: rotate(270deg) scale(1.05); }
              100% { transform: rotate(720deg) scale(1); }
            }
          `}
        </style>
      </div>
    );
  }
);

export default KenoBall;